import React, { Component } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { HTTPService } from "../../../services/httpService";
import ServicesAPI from "../../../services/servicesApiV2";
import { ASSET_TYPES } from '../../../util/assetType';
import { isMarineVisible } from '../../../util/marineCapability';

const API = new HTTPService();

export class TemplateModal extends Component {
    state = {
        templateName: "",
        selectedServices: new Set(),
        activeTab: "",
        serviceCategories: [],
        // Which MARKET the listed services sell into. 'auto' | 'marine' —
        // master_services.asset_type. Deliberately Auto/Marine, not Road/Water:
        // Road/Water names the ASSET being worked on, this names the service.
        assetTypeFilter: 'auto',
        validMasterServiceIds: new Set(), // Track valid IDs from the catalog
        loading: false,
        errors: {},
        saving: false
    };

    async componentDidMount() {
        await this.loadServiceCatalog();
    }

    componentDidUpdate(prevProps) {
        // Reset form when modal opens/closes or action changes
        if (this.props.show && !prevProps.show) {
            this.resetForm();
        }

        // Load template data when editing
        if (this.props.action === "edit" && this.props.template !== prevProps.template) {
            this.loadTemplateData();
        }
    }

    // Load service catalog from API (using v2 endpoints)
    async loadServiceCatalog() {
        try {
            this.setState({ loading: true });

            // Load categories, master services, and pricing using new API
            const [categories, masterServices, installerServices] = await Promise.all([
                ServicesAPI.getServiceCategories(),
                ServicesAPI.getMasterServicesByInstaller(this.props.typeId),
                ServicesAPI.getInstallerServices(this.props.typeId)
            ]);

            // Build pricing lookup by master_service_id
            const pricingLookup = {};
            installerServices.forEach(record => {
                const masterId = record.master_service_id;
                if (!pricingLookup[masterId]) {
                    pricingLookup[masterId] = { RETAIL: {}, WHOLE: {} };
                }
                const price = record.price ?? record.service_cost ?? 0;
                const catalog = record.catalog; // RETAIL or WHOLE
                const size = record.vehicle_size; // S, M, L, XL
                if (catalog && size) {
                    pricingLookup[masterId][catalog][size] = price;
                }
            });

            // Build set of valid master service IDs
            const validMasterServiceIds = new Set(masterServices.map(s => s.id));

            // Build category lookup
            const categoryLookup = {};
            categories.forEach(cat => {
                categoryLookup[cat.category_id] = cat.category_name;
            });

            // Filter out Custom services and group by category
            const categoriesMap = {};

            masterServices.forEach(service => {
                const serviceName = service.service_name || '';

                // Skip services with "Custom" in the name
                if (serviceName.toLowerCase().includes('custom')) {
                    return;
                }

                const categoryName = service.category_name || categoryLookup[service.category_id] || 'Other';

                if (!categoriesMap[categoryName]) {
                    categoriesMap[categoryName] = {
                        category_name: categoryName,
                        services: []
                    };
                }

                categoriesMap[categoryName].services.push({
                    master_service_id: service.id,
                    description: service.service_name,
                    coverage_level: service.coverage_level,
                    notes: service.notes,
                    // Kept so the Auto/Marine filter has something to filter ON.
                    // This shaped entry previously dropped it, which is why the
                    // list could only ever show everything at once.
                    asset_type: service.asset_type || 'auto',
                    pricing: pricingLookup[service.id] || { RETAIL: {}, WHOLE: {} }
                });
            });

            const serviceCategories = Object.values(categoriesMap);

            // Seed the tab from the FILTERED list, not the raw one: a category
            // whose services are all marine is not on screen under the default
            // Auto filter, and pointing activeTab at it renders an empty panel
            // with no tab highlighted.
            const firstVisible = serviceCategories.find(c =>
                (c.services || []).some(s => (s.asset_type || 'auto') === this.state.assetTypeFilter)
            );

            this.setState({
                serviceCategories,
                validMasterServiceIds,
                activeTab: firstVisible?.category_name || "",
                loading: false
            });

        } catch (error) {
            console.error("Error loading service catalog:", error);
            console.error("Error details:", error.response?.data);

            this.setState({
                serviceCategories: [],
                loading: false,
                errors: { ...this.state.errors, loadError: 'Failed to load service catalog' }
            });

            // Load mock data as fallback
            this.loadMockServiceData();
        }
    }

    // Mock data for development (remove when API is ready)
    loadMockServiceData() {
        const mockCategories = [
            {
                category_name: "Ceramic Coating",
                services: [
                    { master_service_id: 1, description: "Basic Ceramic Coating" },
                    { master_service_id: 2, description: "Premium Ceramic Coating" },
                    { master_service_id: 3, description: "Ceramic Coating - Full Vehicle" },
                    { master_service_id: 4, description: "Ceramic Coating - Partial" }
                ]
            },
            {
                category_name: "PPF",
                services: [
                    { master_service_id: 5, description: "PPF Full Vehicle" },
                    { master_service_id: 6, description: "PPF Front End" },
                    { master_service_id: 7, description: "PPF Hood" },
                    { master_service_id: 8, description: "PPF Bumper" }
                ]
            },
            {
                category_name: "Window Tint",
                services: [
                    { master_service_id: 54, description: "Front Doors Tint" },
                    { master_service_id: 56, description: "Rear Doors Tint" },
                    { master_service_id: 58, description: "Windshield Tint" }
                ]
            },
            {
                category_name: "Vinyl Wrap",
                services: [
                    { master_service_id: 10, description: "Full Vehicle Wrap" },
                    { master_service_id: 11, description: "Partial Wrap" },
                    { master_service_id: 12, description: "Racing Stripes" }
                ]
            },
            {
                category_name: "Other Services",
                services: [
                    { master_service_id: 13, description: "Chrome Delete" },
                    { master_service_id: 14, description: "Paint Correction" },
                    { master_service_id: 15, description: "Interior Detail" }
                ]
            }
        ];

        //console.log('📝 Loading mock service data as fallback');
        this.setState({ 
            serviceCategories: mockCategories,
            activeTab: mockCategories[0]?.category_name || "",
            loading: false 
        });
    }

    // Reset form to initial state
    resetForm() {
        this.setState({
            templateName: "",
            selectedServices: new Set(),
            activeTab: this.state.serviceCategories[0]?.category_name || "",
            errors: {}
        });
    }

    // Load template data for editing
    loadTemplateData() {
        if (this.props.template && this.props.action === "edit") {
            const { template } = this.props;

            console.log('DEBUG - Template being edited:', template);
            console.log('DEBUG - Template services:', template.services);

            // Load existing template services (they come from the detailed template data)
            const selectedServices = new Set();
            const invalidServices = [];

            if (template.services && Array.isArray(template.services)) {
                template.services.forEach(service => {
                    const serviceId = service.master_service_id;
                    // Only add services that exist in the current catalog
                    if (this.state.validMasterServiceIds.has(serviceId)) {
                        console.log('DEBUG - Adding valid service to selection:', service);
                        selectedServices.add(serviceId);
                    } else {
                        console.warn('DEBUG - Skipping invalid service (not in catalog):', service);
                        invalidServices.push(service);
                    }
                });
            }

            console.log('DEBUG - Valid selected service IDs:', Array.from(selectedServices));
            if (invalidServices.length > 0) {
                console.warn('DEBUG - Template has', invalidServices.length, 'services not in current catalog:', invalidServices);
            }

            this.setState({
                templateName: template.template_name || "",
                selectedServices
            });
        }
    }

    // Handle template name change
    handleNameChange = (event) => {
        this.setState({ 
            templateName: event.target.value,
            errors: { ...this.state.errors, templateName: "" }
        });
    };

    // Handle service selection toggle
    handleServiceToggle = (serviceId) => {
        const newSelected = new Set(this.state.selectedServices);
        if (newSelected.has(serviceId)) {
            newSelected.delete(serviceId);
        } else {
            newSelected.add(serviceId);
        }
        this.setState({ 
            selectedServices: newSelected,
            errors: { ...this.state.errors, services: "" }
        });
    };

    // Handle tab change
    /**
     * Switch which market's services are listed.
     *
     * The active category tab is reset because the categories themselves change
     * — a category that exists for auto may have no marine services at all, and
     * leaving activeTab pointing at a filtered-away tab renders an empty panel
     * with no tab highlighted.
     */
    setAssetTypeFilter = (assetTypeFilter) => {
        if (assetTypeFilter === this.state.assetTypeFilter) return;
        const next = (this.state.serviceCategories || [])
            .filter(c => (c.services || []).some(s => (s.asset_type || 'auto') === assetTypeFilter));
        this.setState({
            assetTypeFilter,
            activeTab: next[0]?.category_name || "",
        });
    };

    handleTabChange = (categoryName) => {
        this.setState({ activeTab: categoryName });
    };

    // Validate form
    validateForm() {
        const errors = {};

        if (!this.state.templateName.trim()) {
            errors.templateName = "Template name is required";
        }

        // Check that at least one VALID service is selected
        const validSelectedCount = Array.from(this.state.selectedServices)
            .filter(id => this.state.validMasterServiceIds.has(id)).length;

        if (this.state.selectedServices.size === 0) {
            errors.services = "At least one service must be selected";
        } else if (validSelectedCount === 0) {
            errors.services = "None of the selected services are valid. Please select services from the catalog.";
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    }

    // Handle form submission
    handleSubmit = async () => {
        if (!this.validateForm()) {
            return;
        }

        this.setState({ saving: true });

        try {
            // Filter selected services to only include valid master service IDs
            const allSelectedServices = Array.from(this.state.selectedServices);
            const validServices = allSelectedServices.filter(id => this.state.validMasterServiceIds.has(id));
            const invalidServices = allSelectedServices.filter(id => !this.state.validMasterServiceIds.has(id));

            if (invalidServices.length > 0) {
                console.warn('DEBUG - Filtering out invalid service IDs:', invalidServices);
            }

            // Ensure we're sending the correct field name
            const templateData = {
                template_name: this.state.templateName.trim(),
                services: validServices, // Only send valid service IDs
                type_id: parseInt(this.props.typeId) // Use type_id to match what backend expects
            };

            console.log('DEBUG - Sending template data to API:', templateData);
            console.log('DEBUG - Service IDs being sent:', templateData.services);
            console.log('DEBUG - Filtered out invalid IDs:', invalidServices);

            if (this.props.action === "edit") {
                // Update existing template
                await API.put(`/templates/${this.props.typeId}/${this.props.template.template_id}`, templateData);
                //console.log('Template update response:', response);
            } else {
                // Create new template
                await API.post('/templates', templateData);
                //console.log('Template creation response:', response);
            }

            // Close modal and refresh parent data
            this.props.onHide();
            if (this.props.onSave) {
                this.props.onSave();
            }

        } catch (error) {
            console.error("Error saving template:", error);
            console.error("Error response:", error.response?.data);
            
            // Handle specific error responses
            let errorMessage = "Failed to save template. Please try again.";
            if (error.response && error.response.data && error.response.data.error) {
                errorMessage = error.response.data.error;
            }
            
            this.setState({ 
                errors: { 
                    submit: errorMessage
                }
            });
        } finally {
            this.setState({ saving: false });
        }
    };

    // Get selected count for a category
    getSelectedCountForCategory(category) {
        return category.services.filter(service => 
            this.state.selectedServices.has(service.master_service_id)
        ).length;
    }

    render() {
        const { show, onHide, action } = this.props;
        const { 
            templateName, 
            selectedServices, 
            activeTab, 
            serviceCategories: allServiceCategories, 
            assetTypeFilter,
            loading, 
            errors, 
            saving 
        } = this.state;

        // Filter by market, then drop categories the filter emptied — a category
        // tab with nothing behind it is worse than no tab. When marine is not
        // visible the filter is inert and every service is 'auto' anyway.
        const serviceCategories = allServiceCategories
            .map(category => ({
                ...category,
                services: (category.services || []).filter(
                    svc => (svc.asset_type || 'auto') === assetTypeFilter
                ),
            }))
            .filter(category => category.services.length > 0);

        const isEditing = action === "edit";
        const totalSelected = selectedServices.size;

        return (
            <Modal show={show} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa' }}>
                    <Modal.Title>
                        {isEditing ? 'Edit Template' : 'Create New Template'}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: 0 }}>
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading service catalog...</p>
                        </div>
                    ) : (
                        <>
                            {/* Template Name Section */}
                            <div className="px-4 py-3 border-bottom bg-light">
                                <Form.Group>
                                    <Form.Label className="font-weight-bold">Template Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter template name..."
                                        value={templateName}
                                        onChange={this.handleNameChange}
                                        isInvalid={!!errors.templateName}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.templateName}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </div>

                            {/* Service Selection Section */}
                            <div className="px-4 py-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0 font-weight-bold">Select Services</h6>
                                    <small className="text-muted">
                                        {totalSelected} service{totalSelected !== 1 ? 's' : ''} selected
                                    </small>
                                </div>

                                {errors.services && (
                                    <div className="alert alert-danger py-2 mb-3">
                                        {errors.services}
                                    </div>
                                )}

                                {errors.loadError && (
                                    <div className="alert alert-warning py-2 mb-3">
                                        {errors.loadError} - Using sample data for demonstration.
                                    </div>
                                )}

                                {/* Auto / Marine — the MARKET a service sells into, which is
                                    what master_services.asset_type records. Road/Water is the
                                    vehicle vocabulary and belongs on the asset picker, not here.

                                    Gated on isMarineVisible for the same reason the vehicle
                                    catalog's toggle is: without it, every automotive installer
                                    gets a Marine tab that is permanently empty, and marine is
                                    still dark-shipped. */}
                                {isMarineVisible() && (
                                    <div className="text-center mb-3">
                                        <div className="jv-segmented" role="group" aria-label="Service asset type">
                                            <button
                                                type="button"
                                                aria-pressed={assetTypeFilter === ASSET_TYPES.AUTO}
                                                className={`jv-segmented__btn ${assetTypeFilter === ASSET_TYPES.AUTO ? 'is-active' : ''}`}
                                                onClick={() => this.setAssetTypeFilter(ASSET_TYPES.AUTO)}
                                            >
                                                Auto
                                            </button>
                                            <button
                                                type="button"
                                                aria-pressed={assetTypeFilter === ASSET_TYPES.MARINE}
                                                className={`jv-segmented__btn ${assetTypeFilter === ASSET_TYPES.MARINE ? 'is-active' : ''}`}
                                                onClick={() => this.setAssetTypeFilter(ASSET_TYPES.MARINE)}
                                            >
                                                Marine
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Category Tabs */}
                                <div style={{ height: '450px' }}>
                                    {serviceCategories.length > 0 ? (
                                        <>
                                            <div className="border-bottom mb-3">
                                                <nav className="nav" style={{ flexWrap: 'nowrap', overflowX: 'auto', borderBottom: '1px solid #dee2e6' }}>
                                                    {serviceCategories.map(category => {
                                                        const categorySelectedCount = this.getSelectedCountForCategory(category);
                                                        const isActive = activeTab === category.category_name;
                                                        
                                                        return (
                                                            <button
                                                                key={category.category_name}
                                                                onClick={() => this.handleTabChange(category.category_name)}
                                                                className="nav-link px-3 py-2"
                                                                style={{ 
                                                                    whiteSpace: 'nowrap', 
                                                                    border: 'none',
                                                                    backgroundColor: 'transparent',
                                                                    color: isActive ? '#007bff' : '#6c757d',
                                                                    fontWeight: isActive ? '600' : '400',
                                                                    position: 'relative',
                                                                    borderBottom: isActive ? '3px solid #007bff' : '3px solid transparent',
                                                                    transition: 'all 0.2s ease-in-out',
                                                                    cursor: 'pointer',
                                                                    outline: 'none',
                                                                    marginBottom: '-1px' // To overlap the border-bottom of the container
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!isActive) {
                                                                        e.target.style.color = '#495057';
                                                                        e.target.style.borderBottomColor = '#dee2e6';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!isActive) {
                                                                        e.target.style.color = '#6c757d';
                                                                        e.target.style.borderBottomColor = 'transparent';
                                                                    }
                                                                }}
                                                            >
                                                                <div className="d-flex align-items-center">
                                                                    <span>{category.category_name}</span>
                                                                    {categorySelectedCount > 0 && (
                                                                        <span 
                                                                            className="badge ml-2" 
                                                                            style={{ 
                                                                                fontSize: '10px',
                                                                                backgroundColor: isActive ? '#007bff' : '#6c757d',
                                                                                color: 'white'
                                                                            }}
                                                                        >
                                                                            {categorySelectedCount}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </nav>
                                            </div>

                                            {/* Tab Content */}
                                            <div style={{ height: '370px', overflowY: 'auto' }}>
                                                {serviceCategories.map(category => (
                                                    activeTab === category.category_name && (
                                                        <div key={category.category_name}>
                                                            {category.services.map(service => {
                                                                const isSelected = selectedServices.has(service.master_service_id);
                                                                const pricing = service.pricing || { RETAIL: {}, WHOLE: {} };
                                                                const sizes = ['S', 'M', 'L', 'XL'];
                                                                const hasRetail = sizes.some(s => pricing.RETAIL[s]);
                                                                const hasWhole = sizes.some(s => pricing.WHOLE[s]);
                                                                const hasPricing = hasRetail || hasWhole;

                                                                const formatPrice = (val) => {
                                                                    if (!val && val !== 0) return '-';
                                                                    return `$${Number(val).toLocaleString()}`;
                                                                };

                                                                return (
                                                                    <div
                                                                        key={service.master_service_id}
                                                                        className="p-3 mb-2"
                                                                        style={{
                                                                            border: '1px solid #e9ecef',
                                                                            borderRadius: '6px',
                                                                            backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
                                                                            borderColor: isSelected ? '#2196f3' : '#e9ecef',
                                                                            cursor: 'pointer',
                                                                            transition: 'all 0.2s ease'
                                                                        }}
                                                                        onClick={() => this.handleServiceToggle(service.master_service_id)}
                                                                    >
                                                                        <div className="d-flex align-items-center">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isSelected}
                                                                                onChange={() => this.handleServiceToggle(service.master_service_id)}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                style={{
                                                                                    marginRight: '12px',
                                                                                    transform: 'scale(1.2)'
                                                                                }}
                                                                            />
                                                                            <label
                                                                                style={{
                                                                                    cursor: 'pointer',
                                                                                    userSelect: 'none',
                                                                                    margin: 0,
                                                                                    fontWeight: isSelected ? '500' : '400',
                                                                                    color: isSelected ? '#1976d2' : '#333',
                                                                                    flex: 1
                                                                                }}
                                                                            >
                                                                                {service.description}
                                                                                {service.coverage_level && (
                                                                                    <span className="badge badge-secondary ml-2" style={{ fontSize: '10px', fontWeight: 'normal' }}>
                                                                                        {service.coverage_level}
                                                                                    </span>
                                                                                )}
                                                                            </label>
                                                                        </div>
                                                                        {hasPricing && (
                                                                            <div style={{ marginTop: '6px' }}>
                                                                                <table style={{ fontSize: '11px', color: '#666', borderCollapse: 'collapse', width: '100%' }}>
                                                                                    <thead>
                                                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                                                            <th style={{ padding: '2px 6px', fontWeight: '600', textAlign: 'left', width: '60px' }}></th>
                                                                                            {sizes.map(s => (
                                                                                                <th key={s} style={{ padding: '2px 6px', fontWeight: '600', textAlign: 'right' }}>{s}</th>
                                                                                            ))}
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {hasRetail && (
                                                                                            <tr>
                                                                                                <td style={{ padding: '2px 6px', fontWeight: '600', color: '#28a745' }}>RETAIL</td>
                                                                                                {sizes.map(s => (
                                                                                                    <td key={s} style={{ padding: '2px 6px', textAlign: 'right' }}>
                                                                                                        {formatPrice(pricing.RETAIL[s])}
                                                                                                    </td>
                                                                                                ))}
                                                                                            </tr>
                                                                                        )}
                                                                                        {hasWhole && (
                                                                                            <tr>
                                                                                                <td style={{ padding: '2px 6px', fontWeight: '600', color: '#007bff' }}>WHOLE</td>
                                                                                                {sizes.map(s => (
                                                                                                    <td key={s} style={{ padding: '2px 6px', textAlign: 'right' }}>
                                                                                                        {formatPrice(pricing.WHOLE[s])}
                                                                                                    </td>
                                                                                                ))}
                                                                                            </tr>
                                                                                        )}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-muted" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted">No services available</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer style={{ backgroundColor: '#f8f9fa' }}>
                    {errors.submit && (
                        <div className="alert alert-danger py-2 mb-2 w-100">
                            {errors.submit}
                        </div>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center w-100">
                        {/* Counts the WHOLE catalog, not the filtered view. Selections
                            persist across the Auto/Marine toggle, so counting only the
                            visible market could read "3 of 2 services selected". */}
                        <small className="text-muted">
                            {totalSelected} of {allServiceCategories.reduce((acc, cat) => acc + cat.services.length, 0)} services selected
                        </small>
                        <div>
                            <Button variant="secondary" onClick={onHide} disabled={saving}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={this.handleSubmit}
                                // A template with no services is not a template.
                                // validateForm still checks this as a backstop, but a
                                // checkbox list spread across category tabs has nowhere
                                // natural to surface an error — so the button is the
                                // right place to say "not yet". The name stays an inline
                                // field error, because a text input does have somewhere
                                // to put one.
                                disabled={saving || loading || serviceCategories.length === 0 || totalSelected === 0}
                                className="ml-2"
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                        {isEditing ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    isEditing ? 'Update Template' : 'Create Template'
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default TemplateModal;